import Diem from '@travelnest/diem';
import { IEvent } from '@travelnest/typewriter';
import { IAvailability } from '../../api/availability';
import { ApiRequestError } from '../../api/errors';
import { idOrLegacyId } from '../../entities/properties/selectors';
import { ActionTypeNames, ActionValues, createActionCreators } from '../../store/actions';
import { ThunkAction } from '../../store/middleware';

export const getEventsByPropertyActions = createActionCreators<
	{ T: 'AVAILABILITY_GET_EVENTS_BY_ID_REQUEST', P: { id: string } },
	{ T: 'AVAILABILITY_GET_EVENTS_BY_ID_RECEIVE', P: { id: string, events: IEvent[] } },
	{ T: 'AVAILABILITY_GET_EVENTS_BY_ID_FAILURE', P: { id: string, error: ApiRequestError } }
>('AVAILABILITY_GET_EVENTS_BY_ID_REQUEST', 'AVAILABILITY_GET_EVENTS_BY_ID_RECEIVE', 'AVAILABILITY_GET_EVENTS_BY_ID_FAILURE');

export const getEventsByPropertyId = (id: string | number): ThunkAction => async (dispatch, getState, { _deprecatedApi: api }) => {
	const propertyId = idOrLegacyId(id);
	dispatch(getEventsByPropertyActions.AVAILABILITY_GET_EVENTS_BY_ID_REQUEST({ id: propertyId }));
	try {
		const events = await api.availabilityV3.getEventsByPropertyId(propertyId);
		dispatch(getEventsByPropertyActions.AVAILABILITY_GET_EVENTS_BY_ID_RECEIVE({ id: propertyId, events }));
	} catch (error) {
		dispatch(getEventsByPropertyActions.AVAILABILITY_GET_EVENTS_BY_ID_FAILURE({ id: propertyId, error }));
		throw error;
	}
};

export const getAvailabilityByPropertyActions = createActionCreators<
	{ T: 'AVAILABILITY_GET_BY_ID_REQUEST', P: { id: string } },
	{ T: 'AVAILABILITY_GET_BY_ID_RECEIVE', P: { id: string, availability: IAvailability[] } },
	{ T: 'AVAILABILITY_GET_BY_ID_FAILURE', P: { id: string, error: Error } }
>('AVAILABILITY_GET_BY_ID_REQUEST', 'AVAILABILITY_GET_BY_ID_RECEIVE', 'AVAILABILITY_GET_BY_ID_FAILURE');

export const getAvailabilityByPropertyId = (id: string | number): ThunkAction => async (dispatch, getState, { _deprecatedApi: api }) => {
	const propertyId = idOrLegacyId(id);
	dispatch(getAvailabilityByPropertyActions.AVAILABILITY_GET_BY_ID_REQUEST({ id: propertyId }));
	try {
		const availability = await api.availability.getByPropertyId(propertyId);
		dispatch(getAvailabilityByPropertyActions.AVAILABILITY_GET_BY_ID_RECEIVE({ id: propertyId, availability }));
	} catch (error) {
		dispatch(getAvailabilityByPropertyActions.AVAILABILITY_GET_BY_ID_FAILURE({ id: propertyId, error }));
		throw error;
	}
};

export const createEventActions = createActionCreators<
	{ T: 'EVENT_CREATE_REQUEST', P: { id: string } },
	{ T: 'EVENT_CREATE_RECEIVE', P: { id: string, events: IEvent[] } },
	{ T: 'EVENT_CREATE_FAILURE', P: { id: string, error: Error } }
>('EVENT_CREATE_REQUEST', 'EVENT_CREATE_RECEIVE', 'EVENT_CREATE_FAILURE');

export const createEvent = (event: IEvent): ThunkAction => async (dispatch, getState, { _deprecatedApi: api }) => {
	const propertyId = idOrLegacyId(event.property_id);
	const patched = { ...event, property_id: propertyId };
	dispatch(createEventActions.EVENT_CREATE_REQUEST({ id: propertyId }));
	try {
		const events = await api.availability.createEvent(patched);
		dispatch(createEventActions.EVENT_CREATE_RECEIVE({ id: propertyId, events }));
	} catch (error) {
		dispatch(createEventActions.EVENT_CREATE_FAILURE({ id: propertyId, error }));
		throw error;
	}
};

export const blockByDateRangeActions = createActionCreators<
	{ T: 'BLOCK_BY_DATE_RANGE_CREATE_REQUEST', P: { id: string } },
	{ T: 'BLOCK_BY_DATE_RANGE_CREATE_RECEIVE', P: { id: string, events: IEvent[] } },
	{ T: 'BLOCK_BY_DATE_RANGE_CREATE_FAILURE', P: { id: string, error: Error } }
>('BLOCK_BY_DATE_RANGE_CREATE_REQUEST', 'BLOCK_BY_DATE_RANGE_CREATE_RECEIVE', 'BLOCK_BY_DATE_RANGE_CREATE_FAILURE');

export const blockByDateRange = (propertyId: string, startOn: Diem, endOn: Diem): ThunkAction =>
	async (dispatch, getState, { _deprecatedApi: api }) => {
		const orLegacy = idOrLegacyId(propertyId);
		dispatch(blockByDateRangeActions.BLOCK_BY_DATE_RANGE_CREATE_REQUEST({ id: orLegacy }));
		try {
			const events = await api.availabilityV3.blockByDateRange(orLegacy, startOn, endOn);
			dispatch(blockByDateRangeActions.BLOCK_BY_DATE_RANGE_CREATE_RECEIVE({ id: orLegacy, events }));
		} catch (error) {
			dispatch(blockByDateRangeActions.BLOCK_BY_DATE_RANGE_CREATE_FAILURE({ id: orLegacy, error }));
			throw error;
		}
	};

export const createEventWithBookingActions = createActionCreators<
	{ T: 'EVENT_WITH_BOOKING_CREATE_REQUEST', P: { id: string } },
	{ T: 'EVENT_WITH_BOOKING_CREATE_RECEIVE', P: { id: string, events: IEvent[] } },
	{ T: 'EVENT_WITH_BOOKING_CREATE_FAILURE', P: { id: string, error: Error } }
>('EVENT_WITH_BOOKING_CREATE_REQUEST', 'EVENT_WITH_BOOKING_CREATE_RECEIVE', 'EVENT_WITH_BOOKING_CREATE_FAILURE');

export const createEventWithBooking = (data: IEvent): ThunkAction => async (dispatch, getState, { _deprecatedApi: api }) => {
	const propertyId = idOrLegacyId(data.property_id);
	const patched = { ...data, property_id: propertyId };
	dispatch(createEventWithBookingActions.EVENT_WITH_BOOKING_CREATE_REQUEST({ id: propertyId }));
	try {
		const event = await api.availability.createEventWithBooking(patched);
		dispatch(createEventWithBookingActions.EVENT_WITH_BOOKING_CREATE_RECEIVE({ id: propertyId, events: [event] }));
	} catch (error) {
		dispatch(createEventWithBookingActions.EVENT_WITH_BOOKING_CREATE_FAILURE({ id: propertyId, error }));
		throw error;
	}
};

export const updateEventActions = createActionCreators<
	{ T: 'EVENT_UPDATE_REQUEST', P: { id: string } },
	{ T: 'EVENT_UPDATE_RECEIVE', P: { id: string, events: IEvent } },
	{ T: 'EVENT_UPDATE_FAILURE', P: { id: string, error: Error } }
>('EVENT_UPDATE_REQUEST', 'EVENT_UPDATE_RECEIVE', 'EVENT_UPDATE_FAILURE');

export const updateEvent = (event: IEvent): ThunkAction => async (dispatch, getState, { _deprecatedApi: api }) => {
	dispatch(updateEventActions.EVENT_UPDATE_REQUEST({ id: event.property_id }));
	try {
		const eventResp = await api.availability.updateEvent(event);
		dispatch(updateEventActions.EVENT_UPDATE_RECEIVE({ id: event.property_id, events: eventResp }));
	} catch (error) {
		dispatch(updateEventActions.EVENT_UPDATE_FAILURE({ id: event.property_id, error }));
		throw error;
	}
};

export const cancelEventActions = createActionCreators<
	{ T: 'EVENT_DELETE_REQUEST', P: { id: string } },
	{ T: 'EVENT_DELETE_RECEIVE', P: { id: string, events: IEvent } },
	{ T: 'EVENT_DELETE_FAILURE', P: { id: string, error: Error } }
>('EVENT_DELETE_REQUEST', 'EVENT_DELETE_RECEIVE', 'EVENT_DELETE_FAILURE');

export const deleteEvent = (event: IEvent): ThunkAction => async (dispatch, getState, { _deprecatedApi: api }) => {
	dispatch(cancelEventActions.EVENT_DELETE_REQUEST({ id: event.property_id }));
	try {
		const events = await api.availability.deleteEvent(event);
		dispatch(cancelEventActions.EVENT_DELETE_RECEIVE({ id: event.property_id, events }));
	} catch (error) {
		dispatch(cancelEventActions.EVENT_DELETE_FAILURE({ id: event.property_id, error }));
		throw error;
	}
};

const actions = {
	...getAvailabilityByPropertyActions,
	...getEventsByPropertyActions,
	...createEventActions,
	...blockByDateRangeActions,
	...createEventWithBookingActions,
	...updateEventActions,
	...cancelEventActions
};

type AvailabilityActions = ActionValues<typeof actions>;
type AvailabilityActionTypes = ActionTypeNames<AvailabilityActions>;
export type Actions = AvailabilityActions[keyof AvailabilityActions];
export const ActionTypes = Object.keys(actions).reduce((types, t) => {
	types[t] = t;
	return types;
}, {} as AvailabilityActionTypes);
